import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useParams } from "react-router"
import {
  SearchResultNode,
  useSearchSliders,
} from "src/hooks/use-search-sliders"

import {
  TeaserDimension,
  TeaserDimensions,
  TTeaser,
} from "../components/Sliders/Slider"
import { FacetType, SliderLayout, SliderPageQuery } from "../generated/graphql"
import { BrowsePage } from "../screens/Browse/BrowseScreen"
import { mapTeaserDimensions } from "../screens/Detail/DetailScreen"
import { useFocusHelper } from "../utilities/use-focus-helper"

import { useMystageSliders } from "./use-mystage-sliders"
import useSdk, { ErrorLevel } from "./use-sdk"
import { useSlidersV1 } from "./use-sliders-v1"
import useTranslate from "./use-translate"

export enum MystageSliderId {
  RecentlyPlayedVideo = "RecentlyPlayedVideo",
  RecentlyPlayedAudio = "RecentlyPlayedAudio",
  FavoriteVideo = "FavoriteVideo",
  FavoriteAudio = "FavoriteAudio",
  FavoriteEditorialPlaylist = "FavoriteEditorialPlaylist",
}

export const useBrowseScreen = () => {
  const { id, cursor: cursorParam, type: pageType } = useParams()
  const type = pageType?.split("-")?.[0]
  const sdk = useSdk(ErrorLevel.WithPrompt)
  const [data, setData] = useState<BrowsePage>()
  const t = useTranslate()
  const isMyStageQuery =
    id && (Object.values(MystageSliderId) as string[]).includes(id)
  const isFacetQuery =
    !!type && (Object.values(FacetType) as string[]).includes(type)
  const facetType = isFacetQuery ? (type as FacetType) : undefined
  const isTextQuery = !!type && pageType !== "0" && type === "SEARCHTEXT"
  const isSliderQuery = !isFacetQuery && !isTextQuery && !isMyStageQuery
  const node =
    isFacetQuery || isTextQuery
      ? (pageType?.split("-")[2] as SearchResultNode)
      : undefined
  const location = useLocation()
  const memoSliders = useMemo(
    () =>
      data && "slider" in data && !!data.slider ? [data.slider] : undefined,
    [data],
  )
  // sliderPageQuery still uses the old "Slider" type instead of "ListSlider"
  const sliders = useSlidersV1(memoSliders)

  const cursor = useRef(cursorParam)

  const { colSpacing, dimensionId } = useMemo(() => {
    if (!!type && pageType && pageType !== "0") {
      const realType = pageType.split("-")[1]
      const dimensionId = mapTeaserDimensions(realType)
      const { colSpacing } = TeaserDimensions[dimensionId as TeaserDimension]
      return { colSpacing, dimensionId }
    }
    if (!(data && "slider" in data)) {
      const dimension =
        id === MystageSliderId.RecentlyPlayedAudio ||
        id === MystageSliderId.FavoriteAudio
          ? TeaserDimension.SquareSmallTeaser
          : TeaserDimension.LandscapeSmallTeaser
      return {
        colSpacing: TeaserDimensions[dimension].colSpacing,
        dimensionId: dimension,
      }
    }
    if (!data?.slider?.sliderItems?.edges[0])
      return { colSpacing: undefined, dimemsionId: undefined }
    const dimensionId = mapTeaserDimensions(
      data.slider?.sliderItems.edges[0].node.__typename,
    )
    const { colSpacing } = TeaserDimensions[dimensionId as TeaserDimension]
    return { colSpacing, dimensionId }
  }, [data])

  const columnCount = useMemo(() => {
    if (!dimensionId) return 0
    if (dimensionId === TeaserDimension.LandscapeLargeTeaser) return 3
    else if (dimensionId === TeaserDimension.LandscapeSmallTeaser) return 4
    else if (dimensionId === TeaserDimension.AvatarLabel) return 6
    else if (dimensionId === TeaserDimension.LandscapeSmallTeaserNoText)
      return 4
    else return 5
  }, [dimensionId])

  const { getMetaInfo } = useFocusHelper()

  const loadedCursors = useRef<(string | undefined)[]>([])
  const [spinner, setSpinner] = useState(true)
  const loadSliders = useCallback(() => {
    if (!id || !isSliderQuery || loadedCursors.current.includes(cursor.current))
      return
    loadedCursors.current.push(cursor.current)
    const meta = getMetaInfo<{ selected: number }>()
    const sliderPageSize =
      meta && "selected" in meta && meta.selected > 0 ? 9999 : 50
    sdk
      .sliderPage({
        sliderId: id,
        sliderPageSize,
        cursor: cursor.current === "0" ? undefined : cursor.current,
      })
      .then((res) => {
        if (res.slider?.sliderItems?.pageInfo.endCursor)
          cursor.current = res.slider?.sliderItems?.pageInfo.endCursor
        const dataNew = !data
          ? res
          : (Object.assign({}, data) as SliderPageQuery)
        if (
          dataNew.slider?.sliderItems?.edges &&
          res.slider?.sliderItems?.edges
        )
          dataNew.slider.sliderItems.edges = [
            ...dataNew.slider.sliderItems.edges,
            ...res.slider.sliderItems.edges,
          ]
        setData(dataNew)
        setSpinner(false)
      })
  }, [id, data])

  const metaData = useMemo(() => {
    const mInfo = getMetaInfo<{ selected: number; data: BrowsePage }>()
    return mInfo && "data" in mInfo ? mInfo.data : false
  }, [])
  useEffect(() => {
    if (id) {
      setData(undefined)
      setSpinner(true)
      if (isMyStageQuery)
        sdk
          .myStagePage({
            sliderPageSize: 999,
            cursor: undefined,
          })
          .then((res) => {
            setData(res)
            setSpinner(false)
          })
      else if (metaData === false)
        if (isFacetQuery && facetType) {
          // we check in run-time in the previous step that the facetType is valid
          sdk
            .searchByFacetValue({
              sliderPageSize: 999,
              type: facetType,
              value: id,
            })
            .then((res) => {
              setData(res)
              setSpinner(false)
            })
        } else if (isTextQuery) {
          sdk.searchByText({ sliderPageSize: 999, input: id }).then((res) => {
            setData(res)
            setSpinner(false)
          })
        } else loadSliders()
      else if (metaData !== undefined) {
        setData(metaData)
        setSpinner(false)
      }
    }
  }, [location, metaData])

  const myStageData =
    data && isMyStageQuery && "currentUser" in data ? data : undefined
  const myStageSliders = useMystageSliders(
    // add random fallback if the query is malformed
    (id ?? MystageSliderId.FavoriteVideo) as MystageSliderId,
    "undefined",
    myStageData
      ? id === MystageSliderId.FavoriteVideo
        ? myStageData.currentUser?.videoFavorites
        : id === MystageSliderId.FavoriteAudio
          ? myStageData.currentUser?.albumFavorites
          : id === MystageSliderId.RecentlyPlayedVideo
            ? myStageData.currentUser?.videoRecentlyPlayed
            : id === MystageSliderId.RecentlyPlayedAudio
              ? myStageData.currentUser?.albumRecentlyPlayed
              : undefined
      : undefined,
  )

  const facetData =
    data &&
    (isFacetQuery || isTextQuery) &&
    ("searchByFacetValue" in data || "searchByText" in data)
      ? data
      : undefined
  const facetSliders = useSearchSliders(facetData)

  return useMemo(() => {
    let headline: string | undefined,
      teasers: TTeaser[] | undefined,
      totalResults: number | undefined,
      sliderLayout: SliderLayout | undefined
    if (isMyStageQuery) {
      teasers = myStageSliders?.teasers
      headline =
        id === MystageSliderId.FavoriteVideo
          ? t("tv__myStage_headline_favourite_videos")
          : id === MystageSliderId.FavoriteAudio
            ? t("tv__myStage_headline_favourite_albums")
            : id === MystageSliderId.RecentlyPlayedVideo
              ? t("tv__myStage_headline_recent_videos")
              : id === MystageSliderId.RecentlyPlayedAudio
                ? t("tv__myStage_headline_recent_albums")
                : ""
      totalResults =
        data && "currentUser" in data
          ? id === MystageSliderId.RecentlyPlayedVideo
            ? data?.currentUser?.videoRecentlyPlayed?.edges.length
            : id === MystageSliderId.RecentlyPlayedAudio
              ? data?.currentUser?.albumRecentlyPlayed?.edges.length
              : id === MystageSliderId.FavoriteVideo
                ? data?.currentUser?.videoFavorites?.edges.length
                : id === MystageSliderId.FavoriteAudio
                  ? data?.currentUser?.albumFavorites?.edges.length
                  : undefined
          : undefined
    } else if ((isFacetQuery || isTextQuery) && facetSliders) {
      const tSliders = facetSliders.filter((fS) => fS.node === node)[0]
      totalResults = tSliders.totalResults
      teasers = tSliders.teasers
      headline = tSliders.title
    } else if (isSliderQuery && sliders && data && "slider" in data) {
      teasers = sliders[0].teasers
      headline = data.slider?.headline
      totalResults = data?.slider?.sliderItems?.totalResults
      sliderLayout = data.slider?.layout
    }
    return {
      teasers,
      totalResults,
      headline,
      columnCount,
      colSpacing,
      dimensionId,
      load: loadSliders,
      spinner,
      data,
      sliderLayout,
    }
  }, [data, sliders, facetSliders, myStageSliders])
}
